import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { addNotification } from '../store/slices/notificationSlice';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { usePageMetadata } from '../hooks/usePageMetadata';
import UserAvatar from '../components/common/UserAvatar';

import { Badge } from '../components/ui/Badge';
import {
  User,
  Mail,
  Phone,
  Camera,
  Eye,
  EyeOff,
  Shield,
  CheckCircle,
  AlertTriangle,
  CreditCard,
  Building2
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config/api';
import { useGetProfileQuery, useUpdateProfileMutation } from '../store/slices/apiSlice';

const profileSchema = yup.object({
  name: yup.string().required('Name is required'),
  mobile_number: yup.string().required('Mobile number is required'),
});

const passwordSchema = yup.object({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number')
    .required('New password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm password is required'),
});

type ProfileFormData = yup.InferType<typeof profileSchema>;
type PasswordFormData = yup.InferType<typeof passwordSchema>;


const Profile: React.FC = () => {
  const { user, token } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Determine if user is regular user/dealer or admin
  const isRegularUser = user?.role === 'USER' || user?.role === 'DEALER';
  const [activeTab, setActiveTab] = useState<'personal' | 'security' | 'profile' | 'bank'>(
    isRegularUser ? 'profile' : 'personal'
  );

  // Set page metadata
  usePageMetadata('profile');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Use RTK Query for profile
  const { data: profile, refetch, isLoading: isProfileLoading } = useGetProfileQuery({ role: user?.role || 'AU' });
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

  const profileForm = useForm<ProfileFormData>({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name: '',
      mobile_number: '',
    },
  });

  const passwordForm = useForm<PasswordFormData>({
    resolver: yupResolver(passwordSchema),
  });

  useEffect(() => {
    if (profile?.data) {
      profileForm.reset({
        name: profile.data.name || '',
        mobile_number: profile.data.mobile_number || '',
      });
    }
  }, [profile, profileForm]);

  const handleProfileUpdate = async (data: ProfileFormData) => {
    try {
      const formData = new FormData();

      // Only send fields that the API expects (matching curl request)
      formData.append('name', data.name);
      formData.append('mobile_number', data.mobile_number);

      // Add file only if selected
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      await updateProfile({ formData }).unwrap();
      toast.success('Profile updated successfully!');
      refetch();
      setSelectedFile(null);
      dispatch(addNotification({
        title: 'Profile Updated',
        message: 'Your profile information has been successfully updated.',
        type: 'success',
      }));
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update profile');
    }
  };

  const handlePasswordChange = async (data: PasswordFormData) => {
    try {
      const endpoint = (user?.role || 'AU') === 'SU' ? '/super-admin/change-password' : '/admin/change-password';
      const response = await axios.post(`${API_BASE_URL}${endpoint}`, {
        current_password: data.currentPassword,
        new_password: data.newPassword,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.success === true && response.data?.status === 200) {
        toast.success('Password changed successfully!');
        passwordForm.reset();
        dispatch(addNotification({
          title: 'Password Updated',
          message: 'Your password has been successfully changed.',
          type: 'success',
        }));
      } else {
        toast.error(response?.data?.message || 'Failed to change password');
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to change password. Please try again later.';
      toast.error(message);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'SU':
        return <Badge variant="danger" className="text-xs">Super Admin</Badge>;
      case 'AU':
        return <Badge variant="info" className="text-xs">Admin</Badge>;
      case 'DEALER':
        return <Badge variant="warning" className="text-xs">Dealer</Badge>;
      case 'USER':
        return <Badge variant="secondary" className="text-xs">User</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">User</Badge>;
    }
  };

  const TabButton: React.FC<{
    icon: React.ReactNode;
    label: string;
    isActive: boolean;
    onClick: () => void;
  }> = ({ icon, label, isActive, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm font-medium transition-colors ${isActive
        ? 'bg-blue-100 text-blue-700 border border-blue-200'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
        }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600 text-sm">Please log in to view your profile</div>
      </div>
    );
  }

  if (isProfileLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Profile Settings</h1>
          <p className="text-sm text-gray-600">Manage your account information and preferences</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* User Info Card */}
        <div className="lg:col-span-1">
          <Card className="p-4">
            <div className="text-center space-y-4">
              {/* Avatar Section */}
              <div className="relative mx-auto">
                <UserAvatar 
                  src={
                    selectedFile
                      ? URL.createObjectURL(selectedFile)
                      : profile?.data?.profile_image
                  }
                  name={profile?.data?.name || user?.name}
                  size="xl"
                  className="mx-auto border-4 border-white shadow-lg"
                />
                <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-1.5 rounded-full hover:bg-blue-700 transition-colors cursor-pointer shadow-lg">
                  <Camera className="h-3 w-3" />
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </label>
              </div>

              {/* User Details */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  {profile?.data?.name || user?.name || 'User Name'}
                </h3>
                <p className="text-sm text-gray-600">
                  {profile?.data?.email || user?.email || 'user@example.com'}
                </p>
                <div className="flex justify-center">
                  {getRoleBadge(user?.role || '')}
                </div>
              </div>

              {/* File Upload Info */}
              <div className="text-xs text-gray-500 space-y-1">
                <p>Click camera icon to change profile picture</p>
                <p>JPG, GIF or PNG. 1MB max.</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2">
          <Card className="p-4">
            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-2 mb-6">
              {isRegularUser ? (
                <>
                  <TabButton
                    icon={<User className="w-4 h-4" />}
                    label="Profile Info"
                    isActive={activeTab === 'profile'}
                    onClick={() => setActiveTab('profile')}
                  />
                  <TabButton
                    icon={<CreditCard className="w-4 h-4" />}
                    label="Bank Account"
                    isActive={activeTab === 'bank'}
                    onClick={() => setActiveTab('bank')}
                  />
                </>
              ) : (
                <>
                  <TabButton
                    icon={<User className="w-4 h-4" />}
                    label="Personal Info"
                    isActive={activeTab === 'personal'}
                    onClick={() => setActiveTab('personal')}
                  />
                  <TabButton
                    icon={<Shield className="w-4 h-4" />}
                    label="Security"
                    isActive={activeTab === 'security'}
                    onClick={() => setActiveTab('security')}
                  />
                </>
              )}
            </div>

            {/* Tab Content */}
            <div className="space-y-4">
              {/* Regular User Profile Info Tab */}
              {isRegularUser && activeTab === 'profile' && (
                <form onSubmit={profileForm.handleSubmit(handleProfileUpdate)} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          {...profileForm.register('name')}
                          placeholder="Enter your full name"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {profileForm.formState.errors.name && (
                        <p className="text-xs text-red-500 mt-1">{profileForm.formState.errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="email"
                          value={profile?.data?.email || user?.email || ''}
                          className="w-full pl-10 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-500 text-sm cursor-not-allowed"
                          disabled
                          readOnly
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Mobile Number *
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="tel"
                          {...profileForm.register('mobile_number')}
                          placeholder="Enter your mobile number"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {profileForm.formState.errors.mobile_number && (
                        <p className="text-xs text-red-500 mt-1">{profileForm.formState.errors.mobile_number.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Sticky bottom buttons */}
                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      className="w-full sm:w-auto text-sm px-4 py-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isUpdating}
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                    >
                      {isUpdating ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Regular User Bank Account Tab */}
              {isRegularUser && activeTab === 'bank' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Building2 className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-semibold text-blue-900 mb-1">Bank Account Information</h3>
                        <p className="text-xs text-blue-700">
                          Manage your bank account details for transactions and withdrawals.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="text-center py-8">
                      <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">No Bank Account Added</h3>
                      <p className="text-xs text-gray-600 mb-4">
                        Add your bank account to enable transactions and withdrawals.
                      </p>
                      <Button
                        type="button"
                        className="bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                        onClick={() => {
                          toast('Bank account feature coming soon!');
                        }}
                      >
                        Add Bank Account
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Admin Personal Info Tab */}
              {!isRegularUser && activeTab === 'personal' && (
                <form onSubmit={profileForm.handleSubmit(handleProfileUpdate)} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          {...profileForm.register('name')}
                          placeholder="Enter your full name"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {profileForm.formState.errors.name && (
                        <p className="text-xs text-red-500 mt-1">{profileForm.formState.errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="email"
                          value={profile?.data?.email || user?.email || ''}
                          className="w-full pl-10 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-500 text-sm cursor-not-allowed"
                          disabled
                          readOnly
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Mobile Number *
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="tel"
                          {...profileForm.register('mobile_number')}
                          placeholder="Enter your mobile number"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {profileForm.formState.errors.mobile_number && (
                        <p className="text-xs text-red-500 mt-1">{profileForm.formState.errors.mobile_number.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Sticky bottom buttons */}
                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      className="w-full sm:w-auto text-sm px-4 py-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isUpdating}
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                    >
                      {isUpdating ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              )}

              {activeTab === 'security' && (
                <form onSubmit={passwordForm.handleSubmit(handlePasswordChange)} className="space-y-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Current Password *
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          {...passwordForm.register('currentPassword')}
                          placeholder="Enter current password"
                          className="w-full pr-10 pl-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {passwordForm.formState.errors.currentPassword && (
                        <p className="text-xs text-red-500 mt-1">{passwordForm.formState.errors.currentPassword.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        New Password *
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          {...passwordForm.register('newPassword')}
                          placeholder="Enter new password"
                          className="w-full pr-10 pl-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {passwordForm.formState.errors.newPassword && (
                        <p className="text-xs text-red-500 mt-1">{passwordForm.formState.errors.newPassword.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Confirm New Password *
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          {...passwordForm.register('confirmPassword')}
                          placeholder="Confirm new password"
                          className="w-full pr-10 pl-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {passwordForm.formState.errors.confirmPassword && (
                        <p className="text-xs text-red-500 mt-1">{passwordForm.formState.errors.confirmPassword.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Password Requirements */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3 text-sm">Password Requirements</h4>
                    <ul className="text-sm text-gray-700 space-y-2">
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>At least 8 characters long</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>One uppercase letter, one lowercase letter, and one number</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                        <span>Must be different from current password</span>
                      </li>
                    </ul>
                  </div>

                  {/* Sticky bottom buttons */}
                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      className="w-full sm:w-auto text-sm px-4 py-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={passwordForm.formState.isSubmitting}
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                    >
                      {passwordForm.formState.isSubmitting ? 'Changing Password...' : 'Change Password'}
                    </Button>
                  </div>
                </form>
              )}


            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;